import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { superAdminService, EmpresaResumen, PlanItem } from '../../services/superAdminService';
import { useTenantStore } from '../../store/tenantStore';
import { formatRut, cleanRut } from '../../utils/rut';
import { useNavigate, Link } from 'react-router-dom';

const EmpresasPage: React.FC = () => {
  const { setTenant } = useTenantStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: empresas, isLoading, isError, refetch, isFetching } = useQuery<EmpresaResumen[]>({
    queryKey: ['superEmpresasResumen'],
    queryFn: superAdminService.getEmpresasResumen,
  });
  const { data: planes } = useQuery<PlanItem[]>({ queryKey: ['superPlanes'], queryFn: superAdminService.getPlanes });
  const { data: suscripciones } = useQuery<any[]>({ queryKey: ['superSuscripciones'], queryFn: superAdminService.getSuscripciones });

  const createMutation = useMutation({
    mutationFn: (payload: { nombre: string; rut: string }) => superAdminService.createEmpresa(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superEmpresasResumen'] }),
  });
  const updateEmpresa = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: { nombre: string; slug: string; activo: boolean; rut?: string } }) => superAdminService.updateEmpresa(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superEmpresasResumen'] }),
  });
  const deleteEmpresa = useMutation({
    mutationFn: (id: number) => superAdminService.deleteEmpresa(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superEmpresasResumen'] }),
  });
  // Nota: la asignación de plan se realiza inline en el botón "Asignar" más abajo.

  const [nombre, setNombre] = React.useState('');
  const [rut, setRut] = React.useState('');
  const [createError, setCreateError] = React.useState<string | null>(null);

  const [editId, setEditId] = React.useState<number | null>(null);
  const [editNombre, setEditNombre] = React.useState('');
  const [editSlug, setEditSlug] = React.useState('');
  const [editRut, setEditRut] = React.useState('');
  const [seleccionPlan, setSeleccionPlan] = React.useState<Record<number, number>>({});
  const [periodo, setPeriodo] = React.useState<Record<number, string>>({});
  const [habilitada, setHabilitada] = React.useState<Record<number, boolean>>({});

  const empresasOrdenadas = (empresas || []).slice().sort((a,b) => (b.areas - a.areas) || (b.pisos - a.pisos));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando empresas...</div>
        </div>
      </div>
    );
  }

  if (isError || !empresas) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Error al cargar las empresas</div>
        <button 
          onClick={() => refetch()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Empresas</h2>
          <p className="text-gray-600 mt-1">Gestiona las empresas del sistema</p>
        </div>
        <button 
          onClick={() => refetch()} 
          disabled={isFetching}
          className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
        >
          {isFetching ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {/* Create Form */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
        <div className="font-semibold text-gray-900 mb-4">Crear nueva empresa</div>
        {createError && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">{createError}</div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <input 
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
              placeholder="Nombre de la empresa" 
              value={nombre} 
              onChange={e=>setNombre(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">RUT empresa</label>
            <input 
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
              placeholder="12.345.678-9" 
              value={rut} 
              onChange={e=>setRut(formatRut(e.target.value))} 
            />
          </div>
          <div className="sm:col-span-2 flex items-end">
            <button 
              className="w-full px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors" 
              disabled={!nombre || !rut || createMutation.isPending}
              onClick={async ()=>{
                setCreateError(null);
                try {
                  await createMutation.mutateAsync({ nombre, rut: cleanRut(rut) });
                  setNombre('');
                  setRut('');
                } catch (err: any) {
                  const msg = err?.response?.data?.message || 'No se pudo crear la empresa';
                  setCreateError(msg);
                }
              }}
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Empresa'}
            </button>
          </div>
        </div>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {empresasOrdenadas.map(e => {
          const activa = (suscripciones || []).filter(s => s.empresaId === e.id && s.estado === 'Activa').sort((a,b)=> new Date(b.fechaInicio).getTime()-new Date(a.fechaInicio).getTime())[0];
          const planNombre = activa?.planNombre || 'Sin plan';
          return (
            <div key={e.id} className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow p-6">
              {/* Company Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  {editId === e.id ? (
                    <div className="space-y-2">
                      <input 
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                        value={editNombre} 
                        onChange={ev=>setEditNombre(ev.target.value)} 
                      />
                      <input 
                        className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                        value={editSlug} 
                        onChange={ev=>setEditSlug(ev.target.value)} 
                      />
                      <input 
                        className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                        placeholder="RUT empresa"
                        value={editRut} 
                        onChange={ev=>setEditRut(formatRut(ev.target.value))} 
                      />
                    </div>
                  ) : (
                    <div>
                      <div className="font-bold text-gray-900 text-lg">{e.nombre}</div>
                      <div className="text-sm text-gray-500 font-mono">{e.slug}{e.rut ? ` · RUT: ${formatRut(e.rut)}` : ''}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Plan Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-600 mb-1">Plan actual</div>
                <div className="font-semibold text-gray-900">{planNombre}</div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-5 gap-2 mb-6">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-blue-600 font-medium">Pisos</div>
                  <div className="text-lg font-bold text-blue-900">{e.pisos}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-green-600 font-medium">Áreas</div>
                  <div className="text-lg font-bold text-green-900">{e.areas}</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-purple-600 font-medium">Tareas</div>
                  <div className="text-lg font-bold text-purple-900">{e.tareas}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-orange-600 font-medium">Tipos</div>
                  <div className="text-lg font-bold text-orange-900">{e.tiposArea}</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-red-600 font-medium">Registros</div>
                  <div className="text-lg font-bold text-red-900">{e.registros}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-3">
                {editId === e.id ? (
                  <div className="flex gap-2">
                    <button 
                      className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors" 
                      onClick={async ()=>{ await updateEmpresa.mutateAsync({ id: e.id, payload: { nombre: editNombre, slug: editSlug, activo: true, rut: editRut ? cleanRut(editRut) : undefined } }); setEditId(null); }}
                    >
                      Guardar
                    </button>
                    <button 
                      className="flex-1 px-3 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors" 
                      onClick={()=>setEditId(null)}
                    >
                      Cancelar
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    <button 
                      className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors" 
                      onClick={()=>{ setEditId(e.id); setEditNombre(e.nombre); setEditSlug(e.slug); setEditRut(e.rut || ''); }}
                    >
                      Editar
                    </button>
                    <button 
                      className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" 
                      onClick={() => { setTenant(e.id, e.nombre); navigate('/app', { replace: true }); }}
                    >
                      Entrar
                    </button>
                    <Link 
                      to={`/super-admin/empresas/${e.id}/usuarios`} 
                      className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-center"
                    >
                      Usuarios
                    </Link>
                    <button
                      className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60"
                      disabled={deleteEmpresa.isPending}
                      onClick={async ()=>{
                        const confirmar = window.confirm(`¿Eliminar la empresa "${e.nombre}" y todos sus datos relacionados? Esta acción es permanente.`);
                        if (!confirmar) return;
                        try {
                          await deleteEmpresa.mutateAsync(e.id);
                        } catch (err) {
                          // Silencioso, la notificación global o logs manejarán errores
                        }
                      }}
                    >
                      {deleteEmpresa.isPending ? 'Eliminando...' : 'Eliminar'}
                    </button>
                  </div>
                )}

                {/* Plan Assignment */}
                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <select 
                      className="flex-1 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                      value={seleccionPlan[e.id] || ''} 
                      onChange={ev=>setSeleccionPlan(prev=>({ ...prev, [e.id]: Number(ev.target.value) }))}
                    >
                      <option value="">Seleccionar plan...</option>
                      {(planes||[]).map(p => (<option key={p.id} value={p.id}>{p.nombre}</option>))}
                    </select>
                    <select 
                      className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" 
                      value={periodo[e.id] || 'Mensual'} 
                      onChange={ev=>setPeriodo(prev=>({ ...prev, [e.id]: ev.target.value }))}
                    >
                      <option value="Mensual">Mensual</option>
                      <option value="Anual">Anual</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-1 text-sm">
                      <input 
                        type="checkbox" 
                        checked={habilitada[e.id] ?? true} 
                        onChange={ev=>setHabilitada(prev=>({ ...prev, [e.id]: ev.target.checked }))} 
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      /> 
                      Habilitada
                    </label>
                    <button 
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors" 
                      disabled={!seleccionPlan[e.id]} 
                      onClick={async ()=>{ 
                        const planId = seleccionPlan[e.id]; 
                        if (!planId) return; 
                        
                        // Buscar suscripción activa existente
                        const suscripcionActiva = (suscripciones || []).find(s => s.empresaId === e.id && s.estado === 'Activa');
                        
                        if (suscripcionActiva) {
                          // Actualizar suscripción existente
                          await superAdminService.updateSuscripcion(suscripcionActiva.id, {
                            estado: 'Activa',
                            planId: planId, // Cambiar al nuevo plan
                            renuevaAutomatico: true,
                            featureOverridesJson: undefined
                          });
                        } else {
                          // Crear nueva suscripción
                          await superAdminService.createSuscripcion({ 
                            empresaId: e.id, 
                            planId, 
                            renuevaAutomatico: true, 
                            featureOverridesJson: undefined 
                          });
                        }
                        
                        // Limpiar selección
                        setSeleccionPlan(prev => {
                          const newState = { ...prev };
                          delete newState[e.id];
                          return newState;
                        });
                      }}
                    >
                      Asignar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmpresasPage;
